import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import {
  AmenityAvailabilityReturn,
  AmenityReturn,
  ReservationReschedulePreviewReturn,
  ReservationReturn,
  ReservationRuleCode,
  ReservationRuleViolation,
} from '../clients/rust-api-client';
import { amenityService, reservationService } from '../clients/ApiClients';
import { formatMoney } from '../utils/billingHelpers';
import { FieldPicker } from '../common/Custom/FieldPicker';
import { RuleViolationsPanel } from './RuleViolationsPanel';
import {
  MOVE_FEE_OUTCOME_LABELS,
  canSubmitOverrides,
  feeChangeText,
  durationMinutes,
  formatDuration,
  formatLocalRange,
  formatMinute,
  formatNights,
  localDay,
  localMinute,
  nightOptions,
  nightsBetween,
  reservationErrorCode,
  reservationErrorMessage,
  ruleViolationsFrom,
  stayTimes,
  toLocal,
  tzLabel,
} from './reservationMeta';
import { checkboxClass, inputClass, labelClass } from './formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';

/**
 * Move a booking in place (P2, #642): same household, same amenity, a new
 * time — the booking keeps its id, history and thread. The server
 * re-checks every rule except the agreement, vehicle and good standing
 * (settled when it was booked) against the new time, ignoring the booking
 * itself, and any rule it breaks comes back to be overridden one code at
 * a time, as on approval.
 *
 * Every pick is previewed (`POST …/reschedule/preview`, which writes
 * nothing) so the rules it breaks and what happens to the fee show
 * before the manager confirms. The grid is fetched with
 * `exclude_reservation_id`, so the booking's own slot reads free.
 *
 * When an approved booking's fee changes with the time, the old charge is
 * reversed and a new one posted. One the household has already paid
 * cannot be reversed automatically: the server refuses (409 FEE_PAID)
 * unless the manager says to go ahead, in which case the paid charge
 * stands and the difference is a refund due.
 */
interface Props {
  open: boolean;
  reservation: ReservationReturn;
  amenity: AmenityReturn | null;
  onClose: () => void;
  onDecided: (updated: ReservationReturn) => void;
}

/** A refusal because the fee was already paid (`code: FEE_PAID`). */
export const isFeePaidConflict = (err: unknown): boolean => reservationErrorCode(err) === 'FEE_PAID';

/**
 * Whether the move needs the "already paid" acknowledgement: the preview
 * refuses it outright, or (once acknowledged) it would return the difference.
 */
export const needsPaidAcknowledgement = (
  preview: Pick<ReservationReschedulePreviewReturn, 'fee_outcome'> | null,
  acknowledged: boolean
): boolean =>
  !!preview &&
  (preview.fee_outcome === 'refused_fee_paid' || (acknowledged && preview.fee_outcome === 'returned'));

export const RescheduleDialog: React.FC<Props> = ({ open, reservation: r, amenity, onClose, onDecided }) => {
  const overnight = amenity?.booking_mode === 'overnight';
  const [day, setDay] = useState(localDay(r.start_local));
  const [start, setStart] = useState<number | null>(localMinute(r.start_local));
  const [duration, setDuration] = useState<number>(durationMinutes(r.start_local, r.end_local) ?? 60);
  const [nights, setNights] = useState<number>(nightsBetween(r.start_local, r.end_local) ?? 1);
  const [party, setParty] = useState(String(r.party_size));
  const [reason, setReason] = useState('');
  const [availability, setAvailability] = useState<AmenityAvailabilityReturn | null>(null);
  const [violations, setViolations] = useState<ReservationRuleViolation[]>([]);
  const [ticked, setTicked] = useState<string[]>([]);
  const [overrideReason, setOverrideReason] = useState('');
  const [feePaidRefused, setFeePaidRefused] = useState(false);
  const [allowPaid, setAllowPaid] = useState(false);
  const [preview, setPreview] = useState<ReservationReschedulePreviewReturn | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDay(localDay(r.start_local));
    setStart(localMinute(r.start_local));
    setDuration(durationMinutes(r.start_local, r.end_local) ?? 60);
    setNights(nightsBetween(r.start_local, r.end_local) ?? 1);
    setParty(String(r.party_size));
    setReason('');
    setViolations([]);
    setTicked([]);
    setOverrideReason('');
    setFeePaidRefused(false);
    setAllowPaid(false);
    setPreview(null);
    setError(null);
  }, [open, r]);

  // The day's start times, for a slot amenity.
  useEffect(() => {
    setAvailability(null);
    if (!open || !amenity || overnight || !day) return;
    let live = true;
    amenityService
      .getAmenityAvailability({
        amenityId: amenity.id,
        date: day,
        homeId: r.home_id,
        excludeReservationId: r.id,
      })
      .then((res) => live && setAvailability(res.data))
      .catch(() => {
        // The time can still be typed; the server checks it either way.
      });
    return () => {
      live = false;
    };
  }, [open, amenity, overnight, day, r.home_id, r.id]);

  const durations = useMemo(() => {
    if (!amenity || overnight) return [];
    const out: number[] = [];
    for (let m = amenity.min_duration_minutes; m <= amenity.max_duration_minutes; m += amenity.slot_increment_minutes) {
      out.push(m);
    }
    if (!out.includes(duration)) out.push(duration);
    return out.sort((a, b) => a - b);
  }, [amenity, overnight, duration]);

  const partyN = Number(party);
  const partyOk = /^\d+$/.test(party) && partyN >= 1;
  const times =
    overnight && amenity?.check_in_minute != null && amenity.check_out_minute != null
      ? stayTimes(day, nights, amenity.check_in_minute, amenity.check_out_minute)
      : start !== null
        ? { start_local: toLocal(day, start), end_local: toLocal(day, start + duration) }
        : null;
  const unchanged = times?.start_local === r.start_local && times?.end_local === r.end_local && partyN === r.party_size;
  const feePaid = feePaidRefused || needsPaidAcknowledgement(preview, allowPaid);

  // Preview each pick: the rules it breaks and what it does to the fee.
  const startLocal = times?.start_local;
  const endLocal = times?.end_local;
  useEffect(() => {
    setPreview(null);
    if (!open || !startLocal || !endLocal || unchanged || !partyOk) return;
    let live = true;
    const timer = setTimeout(() => {
      setPreviewing(true);
      reservationService
        .previewReservationReschedule({
          reservationId: r.id,
          reservationReschedule: {
            start_local: startLocal,
            end_local: endLocal,
            party_size: partyN,
            allow_fee_change_when_paid: allowPaid ? true : null,
          },
        })
        .then((res) => {
          if (!live) return;
          setPreview(res.data);
          setViolations(res.data.violations);
          setTicked((t) => t.filter((c) => res.data.violations.some((v) => v.code === c)));
        })
        .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't check the new time.")))
        .finally(() => live && setPreviewing(false));
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [open, r.id, startLocal, endLocal, unchanged, partyOk, partyN, allowPaid]);

  const canSubmit =
    !!times &&
    partyOk &&
    !unchanged &&
    !saving &&
    !previewing &&
    canSubmitOverrides(violations, ticked, overrideReason) &&
    (!feePaid || allowPaid);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!times || !canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const res = await reservationService.rescheduleReservation({
        reservationId: r.id,
        reservationReschedule: {
          ...times,
          party_size: partyN,
          reason: reason.trim() || null,
          overrides: ticked.length ? (ticked as ReservationRuleCode[]) : null,
          override_reason: ticked.length ? overrideReason.trim() : null,
          allow_fee_change_when_paid: feePaid && allowPaid ? true : null,
        },
      });
      onDecided(res.data);
      onClose();
    } catch (err) {
      const broken = ruleViolationsFrom(err);
      if (broken) {
        setViolations(broken);
        setTicked((t) => t.filter((c) => broken.some((v) => v.code === c)));
      } else if (isFeePaidConflict(err)) {
        setFeePaidRefused(true);
      } else {
        setError(reservationErrorMessage(err, "Couldn't move the reservation."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <DialogTitle>Reschedule reservation</DialogTitle>
        <DialogDescription>
          {r.amenity_name} · {r.home_address} · now {formatLocalRange(r.start_local, r.end_local)}
        </DialogDescription>
        <DialogBody className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className={labelClass}>
              {overnight ? 'Arrival' : 'Day'}
              <input
                type="date"
                value={day}
                onChange={(e) => {
                  setDay(e.target.value);
                  setViolations([]);
                }}
                className={inputClass}
              />
            </label>
            {overnight ? (
              <div className={labelClass}>
                Nights
                <FieldPicker
                  size="sm"
                  className="w-32"
                  label="Nights"
                  value={String(nights)}
                  onChange={(v) => setNights(Number(v))}
                  options={Array.from(new Set([...nightOptions(amenity?.min_nights, amenity?.max_nights), nights]))
                    .sort((a, b) => a - b)
                    .map((n) => ({ value: String(n), label: formatNights(n) }))}
                />
              </div>
            ) : (
              <div className={labelClass}>
                Length
                <FieldPicker
                  size="sm"
                  className="w-36"
                  label="Length"
                  value={String(duration)}
                  onChange={(v) => setDuration(Number(v))}
                  options={durations.map((d) => ({ value: String(d), label: formatDuration(d) }))}
                />
              </div>
            )}
            <label className={labelClass}>
              Party size
              <input
                inputMode="numeric"
                value={party}
                onChange={(e) => setParty(e.target.value.replace(/\D/g, ''))}
                className={clsx(inputClass, 'w-20', !partyOk && 'border-danger')}
              />
            </label>
          </div>

          {!overnight && (
            <fieldset>
              <legend className="mb-1.5 text-[12px] font-medium text-fg-muted">
                Start time <span className="font-normal">· {tzLabel(r.timezone)}</span>
              </legend>
              {availability && availability.starts.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {availability.starts.map((s) => {
                    const m = localMinute(s.start_local);
                    if (m === null) return null;
                    // The grid leaves this booking out, so its own slot reads free.
                    const blocked = s.status === 'booked' || s.status === 'full' || s.status === 'past';
                    return (
                      <button
                        key={s.start_local}
                        type="button"
                        disabled={blocked}
                        aria-pressed={start === m}
                        onClick={() => setStart(m)}
                        className={clsx(
                          'rounded-md border px-2 py-1 text-[12px] tabular-nums',
                          blocked ? 'border-line/50 text-fg-faint line-through' : 'border-line text-fg-secondary hover:bg-fg/5',
                          start === m && '!border-accent !bg-accent/15 !text-fg'
                        )}
                      >
                        {formatMinute(m)}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[13px] text-fg-muted">
                  {availability ? 'No start times listed for this day.' : 'Loading start times…'}
                </p>
              )}
            </fieldset>
          )}

          {times && !unchanged && (
            <div className="space-y-0.5 text-[13px] text-fg-secondary">
              <p>New time: {formatLocalRange(times.start_local, times.end_local)}</p>
              {previewing && <p className="text-fg-muted">Checking the new time…</p>}
              {preview && !previewing && (
                <p>
                  {feeChangeText(preview, formatMoney) && <>Fee {feeChangeText(preview, formatMoney)}. </>}
                  {MOVE_FEE_OUTCOME_LABELS[preview.fee_outcome] ?? ''}
                </p>
              )}
            </div>
          )}

          <label className={labelClass}>
            Reason (optional — recorded on the history)
            <input value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />
          </label>

          <RuleViolationsPanel
            idPrefix="reschedule"
            violations={violations}
            ticked={ticked}
            onTickedChange={setTicked}
            reason={overrideReason}
            onReasonChange={setOverrideReason}
          />
          {feePaid && (
            <div className="space-y-1.5 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2.5 text-[13px]">
              <p className="text-fg-secondary">
                The fee for this booking has already been paid, and the new time changes it. The paid charge
                cannot be reversed automatically.
              </p>
              <label className="flex items-start gap-2 text-fg-secondary">
                <input
                  type="checkbox"
                  className={clsx(checkboxClass, 'mt-0.5')}
                  checked={allowPaid}
                  onChange={(e) => setAllowPaid(e.target.checked)}
                />
                Move it anyway — keep the paid charge, and mark any difference as a refund due
              </label>
            </div>
          )}
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Keep the time
          </Button>
          <Button color="sky" type="submit" disabled={!canSubmit}>
            {saving ? 'Moving…' : ticked.length ? 'Move with overrides' : 'Move booking'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RescheduleDialog;
